import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Flatpickr from 'react-flatpickr';
import moment from 'moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import strings from '../../../strings';
import { FLATPICKR_FORMAT } from '../../../utils';
import MapPropTypes from '../../MapPropTypes';
import Label from './Label';
import Continuous from './Continuous';
import ContinuousWeekly from './ContinuousWeekly';
import NonContinuous from './NonContinuous';
import { FcExpired } from "react-icons/fc";

import 'flatpickr/dist/themes/light.css';

const _DATE_ADVANCE_MAX = 90;
const _WEEK_WRAP = 4;
const _WEEK_MAX = 52;

export default class DurationInput extends Component {
    static propTypes = {
        hasOnlyWeeklyPricing: PropTypes.bool.isRequired,
        isPricingContinuous: PropTypes.bool.isRequired,
        maxMonths: PropTypes.number.isRequired,
        minMonths: PropTypes.number.isRequired,
        monthCount: PropTypes.number.isRequired,
        offerEnd: PropTypes.string.isRequired,
        prices: MapPropTypes.PricingProps.isRequired,
        showWeeks: PropTypes.bool.isRequired,
        startDate: PropTypes.string.isRequired,
        updateMonthCount: PropTypes.func.isRequired,
        updateStartDate: PropTypes.func.isRequired,
        updateDateRange: PropTypes.func.isRequired,
        updateEndDate: PropTypes.func.isRequired,
        updateWeekCount: PropTypes.func.isRequired,
        weekCount: PropTypes.number.isRequired,
        soldto: PropTypes.string,
        endDate: PropTypes.string,
        enterprise_deal: PropTypes.bool,
        dailyPricingEnabled: PropTypes.bool
    };

    constructor() {
        super();
        this.state = { forceOverflowVisible: false };
    }

    _checkOverflow(months, weeks) {
        const offerEnd = moment(this.props.offerEnd);
        const desiredEnd = moment(this.props.startDate)
            .add(months, 'months')
            .add(weeks, 'weeks');
        const overflowEntered = desiredEnd > offerEnd;

        this.setState({ forceOverflowVisible: overflowEntered });

        return overflowEntered;
    }

    _handleMonthCountChange(newValue) {
        let wasReset = false;

        if (newValue > this._getMaxMonths()) {
            wasReset = true;
            newValue = this.props.monthCount;
            this.props.updateWeekCount(0);
        }

        if (newValue != 0 && newValue < this._getMinMonths()) {
            wasReset = true;
            newValue = this.props.monthCount;
        }

        this._checkOverflow(newValue, this.props.weekCount);
        this.props.updateMonthCount(newValue);

        return wasReset;
    }

    _handleWeekCountChange(newValue, isContinousPricing) {
        if (isContinousPricing) {
            const maxWeeks = this._getMaxWeeks();

            if (!this.props.hasOnlyWeeklyPricing && newValue >= maxWeeks) {
                const addMonths = Math.floor(newValue / maxWeeks);
                const wasReset = this._handleMonthCountChange(
                    this.props.monthCount + addMonths
                );

                newValue = wasReset ? maxWeeks - 1 : newValue % maxWeeks;
            }

            this._checkOverflow(this.props.monthCount, newValue);
        }
        this.props.updateWeekCount(newValue);
    }

    _shouldClearCustomEndDate(newStartDate) {
        const soldTo = (this.props.soldto || '').trim();
        const usesEndDatePicker =
            this.props.enterprise_deal ||
            (soldTo.length > 0 && this._isRenderEndDatePicker(soldTo)) ||
            this.props.dailyPricingEnabled;

        return Boolean(
            usesEndDatePicker &&
            this.props.endDate &&
            moment(this.props.endDate).isBefore(newStartDate.clone().add(1, 'day'))
        );
    }

    _handleStartDateChange(event) {
        const newStartDate = moment(event[0]).startOf('day');
        const startDate = newStartDate.toISOString();

        if (this._shouldClearCustomEndDate(newStartDate)) {
            this.props.updateDateRange(startDate, '');
        } else {
            this.props.updateStartDate(startDate);
        }

        if (this._getMaxMonths() < this.props.monthCount) {
            this.props.updateMonthCount(this._getMaxMonths());
        }
    }

    _handleEndDateChange(event) {
        this.props.updateEndDate(event[0].toISOString());
        // if (this._getMaxMonths() < this.props.monthCount) {
        //     this.props.updateMonthCount(this._getMaxMonths());
        // }
    }

    _getMaxMonths() {
        const offerEnd = moment(this.props.offerEnd);
        const offerDelta = moment.duration(
            moment(offerEnd).diff(moment(this.props.startDate))
        );
        const offerMax = Math.ceil(offerDelta.asMonths());

        if (offerMax < this.props.maxMonths) {
            return offerMax;
        }

        return this.props.maxMonths;
    }

    _getMinMonths() {
        return this.props.minMonths;
    }

    _getMaxWeeks() {
        if (this.props.hasOnlyWeeklyPricing) {
            return _WEEK_MAX;
        }

        if (this.props.monthCount === this.props.maxMonths) {
            return 0;
        }

        return _WEEK_WRAP;
    }

    _durationOverflow() {
        return (
            this.state.forceOverflowVisible ||
            moment(this.props.startDate).add(this.props.monthCount, 'months') >
            moment(this.props.offerEnd)
        );
    }

    _getEndDate() {
        let result = moment(this.props.startDate).add(
            this.props.monthCount,
            'months'
        );

        if (this.props.showWeeks) {
            result = result.add(this.props.weekCount, 'weeks');
        }

        return result.format('L');
    }

    _getMaxStartDate() {
        const offerEnd = moment(this.props.offerEnd);
        const advanceMax = moment().add(_DATE_ADVANCE_MAX, 'days');

        if (advanceMax > offerEnd) {
            return offerEnd.toISOString();
        }

        return advanceMax.toISOString();
    }

    _getMaxEndDate() {
        const startDate = moment(this.props.startDate);
        let endDate = startDate.clone();
        if (this.props.hasOnlyWeeklyPricing) {
            // If offer has only weekly pricing, set max end date to 4 weeks from start date
            endDate = endDate.add(4, 'w').toISOString();
        } else if (this.props.dailyPricingEnabled) {
            // If offer has only daily pricing, calculate the end date by adding 12 months to the startDate
            endDate = endDate.add(12, 'months');
        } else {
            // Calculate the end date by adding maxMonths to the startDate
            endDate = endDate.add(this.props.maxMonths, 'months');
        }
        // Ensure the end date does not exceed the offer end date
        const offerEndDate = moment(this.props.offerEnd);
        const restrictedEndDate = endDate.isAfter(offerEndDate) ? offerEndDate : endDate;
        return restrictedEndDate.toISOString();
    }

    _renderDatePicker() {
        return (
            <div>
                <Flatpickr
                    onChange={(e) => this._handleStartDateChange(e)}
                    placeholder={strings.chooseDate}
                    className="form-control datepicker m-r-xs flatpickr-input required"
                    value={this.props.startDate}
                    options={{
                        maxDate: this._getMaxStartDate(),
                        minDate: moment().startOf('day').toISOString(),
                        dateFormat: FLATPICKR_FORMAT,
                    }}
                />
                <Label s="startDate" />
            </div>
        );
    }

    _renderEndDatePicker() {

        const jumpDate = this.props.endDate
                        ? moment(this.props.endDate).startOf('day').toDate()
                        : moment(this.props.startDate).startOf('day').add(1, 'day').toDate();

        return (
            <div style={{ paddingLeft: "0.5em" }}>
                <Flatpickr
                    key={this.props.startDate}
                    onChange={(e) => this._handleEndDateChange(e)}
                    placeholder={strings.chooseDate}
                    className="form-control datepicker m-r-xs flatpickr-input required"
                    value={this.props.endDate}
                    options={{
                        maxDate: this._getMaxEndDate(),
                        minDate: moment(this.props.startDate).startOf('day').add(1, 'day').toDate(),
                        dateFormat: FLATPICKR_FORMAT,
                        onReady: (_, __, instance) => instance.jumpToDate(jumpDate),
                        onOpen: (_, __, instance) => instance.jumpToDate(jumpDate),
                    }}
                />
                <Label s="endDate" />
            </div>
        );
    }



    _renderOverflowWarning() {
        if (!this._isRenderEndDatePicker(this.props.soldto.trim()) && !this.props.enterprise_deal && !this.props.dailyPricingEnabled) {
            if (this._durationOverflow()) {
                const key = String(Math.random());
                const tooltip = (
                    <Tooltip id={key}>{strings.durationOverflow}</Tooltip>
                );

                return (
                    <span>
                        <OverlayTrigger
                            key={key}
                            placement="right"
                            trigger={['hover', 'focus']}
                            overlay={tooltip}
                        >
                            <span className="label label-default text-center display-block overflow-warning">
                                {`End Date: ${this._getEndDate()}       `}
                                <FcExpired />
                            </span>
                        </OverlayTrigger>
                    </span>
                );
            } else {
                return (
                    <span className="label label-default text-center display-block overflow-warning">
                        End Date: {this._getEndDate()}
                    </span>
                );
            }
        }
        return null;
    }

    _isRenderEndDatePicker(soldTo) {
        const eligibleSoldTos = process.env.DATE_RANGE_SOLDTO_ACCOUNTS.split(',')
        return eligibleSoldTos.includes(soldTo)
    }

    _renderInput() {
        const baseAttr = {
            showWeeks: this.props.showWeeks,
            monthCount: this.props.monthCount,
            weekCount: this.props.weekCount,
            updateMonthCount: (newValue) =>
                this._handleMonthCountChange(newValue),
            maxMonths: this._getMaxMonths(),
            minMonths: this - this._getMinMonths(),
            maxWeeks: this._getMaxWeeks(),
            onBlur: () => this.setState({ forceOverflowVisible: false }),
            prices: this.props.prices,
        };
        const soldTo = this.props.soldto.trim()

        if (this.props.enterprise_deal || soldTo.length > 0 && this._isRenderEndDatePicker(soldTo) || this.props.dailyPricingEnabled) {
            return this._renderEndDatePicker();
        }

        if (this.props.hasOnlyWeeklyPricing) {
            return <ContinuousWeekly
                {...baseAttr}
                updateWeekCount={(newValue) => this._handleWeekCountChange(newValue, true)}
            />;
        }

        if (this.props.isPricingContinuous) {
            return <Continuous
                {...baseAttr}
                updateWeekCount={(newValue) => this._handleWeekCountChange(newValue, true)}
            />;
        }

        return <NonContinuous
            {...baseAttr}
            updateWeekCount={(newValue) => this._handleWeekCountChange(newValue, false)}
        />;
    }

    render() {
        return (
            <span className="duration-input">
                <div className="row row-flush m-b-xs">
                    <div className="col-md-6">{this._renderDatePicker()}</div>
                    <div className="col-md-6">{this._renderInput()}</div>
                </div>
                {this._renderOverflowWarning()}
            </span>
        );
    }
}



// WEBPACK FOOTER //
// ./app/components/Cart/DurationInput/index.jsx